import React, { useContext, useEffect, useMemo, useState } from "react";
import { useMutation } from "react-query";
import GlobalContext from "src/context/global-context";
import { useIsMounted } from "src/lib/hooks/use-is-mounted";
import client from "../../api";
import {
  APISomethingWrongMsg,
  NetworkDisconnectMsg,
  permissions as permissionConstants,
} from "src/lib/constants";
import usePermissions from "src/hooks/use-permissions";
import { capitalizeFirstLetter, getErrorMessage, logger } from "src/lib/utils";
import { Table } from "antd";
import ToggleSwitch from "src/components/ui/switch";
import { ColumnsType } from "antd/es/table";
import { Select } from "antd";
import debounce from "lodash.debounce";

const AdminAlerts = () => {
  const { canView, canList, canEdit } = usePermissions(
    permissionConstants.ADMIN_ALERT
  );
  const canViewAlerts = canView || canList;

  const [dataSource, setDataSource] = useState<any[]>([]);
  const { setIsAlertOpened, setAlertText } = useContext(GlobalContext);
  const isMounted = useIsMounted();
  const { Option } = Select;

  const { mutate: getAlertsList, isLoading } = useMutation(
    client.adminAlerts.listAllAlerts,
    {
      onSuccess: (data) => {
        logger.log("data in all list", data);
        setDataSource(
          data.map((item: any) => ({
            key: item.id,
            ...item,
          }))
        );
      },
      onError: (error: any) => {
        if (error.code === "ERR_NETWORK") {
          setAlertText(NetworkDisconnectMsg);
        } else {
          if (error.response) {
            setAlertText(getErrorMessage(error.response.data));
          } else {
            setAlertText(APISomethingWrongMsg);
          }
        }
        setIsAlertOpened(true);
      },
    }
  );

  const { mutate: updateNotification, isLoading: isLoadingUpdate } =
    useMutation(client.adminAlerts.updateAlert, {
      onSuccess: (data) => {
        logger.log("updated data", data);
        getAlertsList();
      },
      onError: (error: any) => {
        if (error.code === "ERR_NETWORK") {
          setAlertText(NetworkDisconnectMsg);
        } else {
          if (error.response) {
            setAlertText(getErrorMessage(error.response.data));
          } else {
            setAlertText(APISomethingWrongMsg);
          }
        }
        setIsAlertOpened(true);
      },
    });

  const onValueChanged = (type: string, value: boolean) => {
    if (!canEdit) {
      setAlertText("You do not have permission to update alert notifications");
      setIsAlertOpened(true);
      return;
    }

    logger.log(type, value);

    // Find the selected row
    let selData = dataSource.find((item) => item.id === Number(type));

    if (!selData) return;

    // Construct the payload for update API
    const payload = {
      id: selData.id,
      module: selData.module,
      emails: selData.emails,
      status: value, // the new toggle value
    };

    setTimeout(() => updateNotification(payload), 100);
  };

  // Function to locally update the dataSource for instant feedback
  const updateLocalDataSource = (id: number, updates: Partial<any>) => {
    setDataSource((prev) =>
      prev.map((item) => (item.id === id ? { ...item, ...updates } : item))
    );
  };

  // 1. Define the debounced function using useMemo
  // This function will call updateNotification, but only once per 1s window
  const debouncedUpdateNotification = useMemo(
    () => debounce((payload) => updateNotification(payload), 1000),
    [updateNotification] // Dependency array includes the updateNotification mutation trigger
  );

  useEffect(() => {
    if (isMounted && canViewAlerts) {
      getAlertsList();
    }
    // 2. Cleanup: Cancel the debounced function when the component unmounts
    return () => {
      debouncedUpdateNotification.cancel();
    };
  }, [isMounted, canViewAlerts, getAlertsList, debouncedUpdateNotification]);

  const columns: ColumnsType<any> = [
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Module name
        </p>
      ),
      dataIndex: "module",
      key: "module",
      className: "w-auto sm:w-[25%]",
      render: (value: string) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-primary text-[14px] font-medium">
            {capitalizeFirstLetter(value)}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Recipient Emails
        </p>
      ),
      dataIndex: "emails",
      key: "emails",
      className: "w-auto",
      render: (emails: string[]) => (
        <div className="flex flex-col gap-1">
          {emails?.map((email, idx) => (
            <span key={idx} className="text-primary text-[14px]">
              {email}
            </span>
          ))}
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Status
        </p>
      ),
      dataIndex: "status",
      key: "status",
      className: "w-auto",
      render: (status, record) => {
        const isDisabled = !canEdit || isLoadingUpdate;
        return (
          <div
            className={`flex flex-row gap-2 items-center ${isDisabled ? "opacity-50 cursor-not-allowed" : ""}`}
            title={!canEdit ? "Read only - No edit permission" : undefined}
          >
            <ToggleSwitch
              type={`${record.id}`}
              value={status}
              onValueChanged={onValueChanged}
              disabled={isDisabled}
            />
          </div>
        );
      },
    },

    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Min Amount
        </p>
      ),
      key: "minAmount",
      className: "w-[150px]",
      render: (_, record) =>
        record.module === "money-mover" ? (
          <input
            type="number"
            disabled={!canEdit}
            className={`border border-gray-300 rounded-md px-2 py-1 w-full text-gray-800 text-[14px] focus:ring-indigo-500 focus:border-indigo-500 transition-colors shadow-sm ${
              !canEdit ? "bg-gray-100 cursor-not-allowed text-gray-500" : ""
            }`}
            value={record.min_amount ?? ""}
            placeholder={canEdit ? "Enter min amount" : "—"}
            onChange={(e) => {
              if (!canEdit) return;
              const newValue = e.target.value;
              const numericValue = newValue ? Number(newValue) : null;

              // 1. Update local state immediately for better UX
              updateLocalDataSource(record.id, { min_amount: numericValue });

              const payload = {
                id: record.id,
                module: record.module,
                status: record.status,
                emails: record.emails,
                min_amount: numericValue,
              };

              // 2. Use the debounced API call
              debouncedUpdateNotification(payload);
            }}
          />
        ) : (
          <span className="text-gray-400 text-[13px] italic">—</span>
        ),
    },

    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Edit Recipients
        </p>
      ),
      key: "selectEmail",
      className: "w-[300px]",
      render: (_, record) => (
        <Select
          // FIX: Change mode="multiple" to mode="tags" to allow user to type in new, arbitrary email values and create a tag on 'Enter'.
          mode="tags"
          allowClear
          showSearch
          placeholder={canEdit ? "Type email(s) & press Enter" : "No edit permission"}
          style={{ width: "100%" }}
          value={record.emails} // pre-select all existing emails
          tokenSeparators={[" ", ","]}
          disabled={!canEdit}
          onChange={(selectedEmails: string[]) => {
            if (!canEdit) return;
            // Filter out any potential duplicates or empty strings that might arise from tagging
            const uniqueEmails = Array.from(
              new Set(selectedEmails.filter((e) => e.trim() !== ""))
            );

            // 1. Update local state immediately
            updateLocalDataSource(record.id, { emails: uniqueEmails });

            // 2. Construct and send payload
            const payload = {
              id: record.id,
              module: record.module,
              status: record.status,
              emails: uniqueEmails,
              min_amount: record.min_amount,
            };
            updateNotification(payload);
          }}
        >
          {/* Include predefined options, but the tags mode allows new ones to be created */}
          {record.emails.map((email: any) => (
            <Option key={email} value={email}>
              {email}
            </Option>
          ))}
        </Select>
      ),
    },
  ];

  return (
    <div className="w-full h-full bg-gray">
      <div className="w-full h-full flex-col p-2 sm:p-4">
        <p className="text-[25px] sm:text-[32px] font-manrope text-primary font-medium leading-[37.5px] sm:leading-[48px]">
          Alert Notifications
        </p>

        {!canViewAlerts ? (
          <div className="flex flex-col items-center justify-center bg-white rounded-[12px] mt-2 sm:mt-4 p-8 text-center shadow-sm">
            <p className="font-manrope text-[16px] font-semibold text-secondary">
              You do not have permission to view alert notifications.
            </p>
          </div>
        ) : (
          <div className="flex flex-col w-full bg-white rounded-[12px] p-4 mt-2 sm:mt-4">
            <Table
              loading={isLoading}
              showSorterTooltip={false}
              columns={columns}
              dataSource={dataSource}
              pagination={false}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminAlerts;
